<template>
  <div class="chat-widget-sequence-picker">
    <div
      class="chat-widget-sequence-picker__dropdown"
      :class="{ 'is-open': dropdownOpen }"
    >
      <button
        type="button"
        class="chat-widget-sequence-picker__trigger"
        :disabled="disabled"
        @click="toggleDropdown"
      >
        <span class="chat-widget-sequence-picker__trigger-icon">
          <i class="bi bi-soundwave" />
        </span>
        <span class="chat-widget-sequence-picker__trigger-text">
          <span class="chat-widget-sequence-picker__trigger-label">{{ activeSource ? activeSource.label : 'Select a sequence' }}</span>
          <span
            v-if="activeSource"
            class="chat-widget-sequence-picker__trigger-count"
          >{{ activeSource.count }} images</span>
        </span>
        <span class="chat-widget-sequence-picker__trigger-chevron">
          <i class="bi bi-chevron-down" />
        </span>
      </button>
      <div
        v-show="dropdownOpen"
        class="chat-widget-sequence-picker__menu"
      >
        <div class="chat-widget-sequence-picker__menu-header">
          IMAGE SEQUENCES LIBRARY
        </div>
        <button
          v-for="src in sources"
          :key="src.id"
          type="button"
          class="chat-widget-sequence-picker__menu-item"
          :class="{ 'is-active': src.id === activeSourceId }"
          @click="selectSource(src.id)"
        >
          <span class="chat-widget-sequence-picker__menu-label">
            {{ src.label }}
            <span class="chat-widget-sequence-picker__menu-count">· {{ src.count }}</span>
          </span>
          <span
            v-if="src.id === activeSourceId"
            class="chat-widget-sequence-picker__menu-check"
          >
            <i class="bi bi-check-lg" />
          </span>
        </button>
      </div>
    </div>

    <div class="chat-widget-sequence-picker__slots">
      <button
        v-for="slot in activeSlots"
        :key="slot.key"
        type="button"
        class="chat-widget-sequence-picker__slot"
        :class="{ 'is-checked': isChecked(slot.key) }"
        :disabled="disabled"
        @click="toggle(slot.key)"
      >
        <span class="chat-widget-sequence-picker__slot-check">
          <i
            v-if="isChecked(slot.key)"
            class="bi bi-check-square-fill"
          />
          <i
            v-else
            class="bi bi-square"
          />
        </span>
        <span class="chat-widget-sequence-picker__slot-label">{{ slot.label }}</span>
      </button>
      <p
        v-if="!activeSlots.length"
        class="chat-widget-sequence-picker__empty"
      >
        No image types for this sequence.
      </p>
    </div>

    <button
      type="button"
      class="btn btn-sm chat-widget-sequence-picker__confirm"
      :class="confirmStateClass"
      :disabled="disabled || !selectedKeys.length || !hasPendingChanges"
      @click="confirm"
    >
      Confirm selection ({{ selectedKeys.length }})
    </button>
  </div>
</template>

<script>
export default {
  name: 'AIChatWidgetSequencePicker',
  props: {
    sources: {
      type: Array,
      required: true,
    },
    initialSourceId: {
      type: String,
      default: '',
    },
    initialSelectedKeys: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const activeSourceId = this.resolveActiveSourceId(this.initialSourceId);
    const initialSource = this.sources.find((s) => s.id === activeSourceId);
    const seededKeys = this.initialSelectedKeys.length
      ? this.initialSelectedKeys.slice()
      : (initialSource ? initialSource.slots.map((s) => s.key) : []);

    return {
      dropdownOpen: false,
      activeSourceId,
      selectedKeys: seededKeys,
      // Snapshot of the selection that was last applied via the Confirm
      // button. Null until the user confirms once, so the initial render
      // reads as "pending" and the button is highlighted.
      lastConfirmedKeys: null,
    };
  },
  computed: {
    activeSource() {
      return this.sources.find((s) => s.id === this.activeSourceId) || null;
    },
    activeSlots() {
      return this.activeSource ? this.activeSource.slots : [];
    },
    hasPendingChanges() {
      if (!this.lastConfirmedKeys) return true;
      if (this.selectedKeys.length !== this.lastConfirmedKeys.length) return true;
      const sortedCurrent = [...this.selectedKeys].sort();
      const sortedLast = [...this.lastConfirmedKeys].sort();
      return sortedCurrent.some((k, i) => k !== sortedLast[i]);
    },
    confirmStateClass() {
      if (!this.selectedKeys.length || !this.hasPendingChanges) {
        return 'is-applied';
      }
      return 'is-pending';
    },
  },
  watch: {
    sources(newSources) {
      if (!newSources.find((s) => s.id === this.activeSourceId)) {
        this.activeSourceId = this.resolveActiveSourceId('');
        this.selectedKeys = this.activeSource
          ? this.activeSource.slots.map((s) => s.key)
          : [];
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown);
    document.addEventListener('keydown', this.onDocumentKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  methods: {
    resolveActiveSourceId(preferredId) {
      if (preferredId && this.sources.find((s) => s.id === preferredId)) {
        return preferredId;
      }
      return this.sources.length ? this.sources[0].id : '';
    },
    toggleDropdown() {
      if (this.disabled) return;
      this.dropdownOpen = !this.dropdownOpen;
    },
    onDocumentMousedown(event) {
      if (!this.dropdownOpen) return;
      if (this.$el && this.$el.contains(event.target)) return;
      this.dropdownOpen = false;
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.dropdownOpen) {
        this.dropdownOpen = false;
      }
    },
    selectSource(id) {
      this.activeSourceId = id;
      this.dropdownOpen = false;
      const src = this.sources.find((s) => s.id === id);
      this.selectedKeys = src
        ? (src.defaultSelectedKeys || src.slots.map((s) => s.key)).slice()
        : [];
    },
    isChecked(key) {
      return this.selectedKeys.includes(key);
    },
    toggle(key) {
      if (this.disabled) return;
      const idx = this.selectedKeys.indexOf(key);
      if (idx >= 0) {
        this.selectedKeys.splice(idx, 1);
      } else {
        this.selectedKeys.push(key);
      }
    },
    confirm() {
      if (!this.selectedKeys.length || !this.hasPendingChanges) return;
      const src = this.activeSource;
      this.lastConfirmedKeys = this.selectedKeys.slice();
      this.$emit('confirm', {
        source: src ? src.source : 'ai',
        sequenceId: src ? src.sequenceId : null,
        keys: this.selectedKeys.slice(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-widget-sequence-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;

  &__dropdown {
    position: relative;
  }

  &__trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.12s, box-shadow 0.12s;

    &:hover:not(:disabled) {
      border-color: #cbd5e1;
    }

    &:focus {
      outline: none;
      border-color: #379C75;
      box-shadow: 0 0 0 3px rgba(55, 156, 117, 0.18);
    }
  }

  &__trigger-icon {
    font-size: 16px;
    color: #379C75;
    display: inline-flex;
  }

  &__trigger-text {
    flex: 1;
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  }

  &__trigger-label {
    font-size: 13.5px;
    font-weight: 600;
    color: #1a1a2e;
  }

  &__trigger-count {
    font-size: 11.5px;
    color: #94a3b8;
  }

  &__trigger-chevron {
    color: #94a3b8;
    display: inline-flex;
  }

  &__menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    z-index: 20;
    max-height: 240px;
    overflow-y: auto;
  }

  &__menu-header {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: #94a3b8;
    padding: 10px 14px 6px;
    text-transform: uppercase;
  }

  &__menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;

    &:hover {
      background: #f1f5f9;
    }

    &.is-active {
      background: #eaf7f2;
    }
  }

  &__menu-label {
    font-size: 13px;
    color: #1a1a2e;
    font-weight: 500;
  }

  &__menu-count {
    color: #94a3b8;
    font-weight: 400;
    margin-left: 4px;
  }

  &__menu-check {
    color: #379C75;
    font-size: 15px;
  }

  &__slots {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__slot {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: #eaf7f2;
    border: 1px solid transparent;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    color: #1a1a2e;
    font-size: 13.5px;
    font-weight: 500;
    transition: background 0.1s, color 0.1s;

    &.is-checked {
      background: #379C75;
      color: #fff;
    }

    &:hover:not(:disabled) {
      filter: brightness(0.98);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__slot-check {
    display: inline-flex;
    font-size: 16px;
  }

  &__slot-label {
    flex: 1;
  }

  &__empty {
    color: #94a3b8;
    font-size: 13px;
    margin: 8px 0;
  }

  &__confirm {
    width: 100%;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13.5px;
    font-weight: 600;
    transition: box-shadow 0.15s, background-color 0.15s, border-color 0.15s;

    // Pending: there are unconfirmed changes — call attention with a green
    // ring around the filled primary button so the user knows they need
    // to click to apply.
    &.is-pending {
      background-color: #379C75;
      border: 1px solid #379C75;
      color: #fff;
      box-shadow: 0 0 0 3px rgba(55, 156, 117, 0.30);

      &:hover:not(:disabled) {
        background-color: #2E8D68;
        border-color: #2E8D68;
      }
    }

    // Applied: the current selection matches what was last confirmed, so
    // there's nothing to apply — render disabled / greyed out.
    &.is-applied {
      background-color: #f1f5f9;
      border: 1px solid #e2e8f0;
      color: #94a3b8;
      cursor: not-allowed;
    }
  }
}
</style>
